import { UPDATE_ACCESS_GROUP } from '@/common/actions/update';
import {
  getGroupRoleSelectOption,
  getUsersAndGroupsPath,
  groupRoleSelectOptions,
} from '@/common/pages/accountEdit/usersAndGroups/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import { Box, Button, Input, SelectSingle, TextArea, TextField } from '@servicepipe/ui-components';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

const validationSchema = Yup.object({
  role: Yup.object({
    value: Yup.string().oneOf(['ENGINEER', 'READ_ONLY']).required('The field is required'),
    label: Yup.string().required(),
  })
    .nullable()
    .required('The field is required'),
  name: Yup.string()
    .required('The field is required')
    .max(50, 'Group name must be shorter than 50 symbols'),
  description: Yup.string().max(300, 'Description must be shorter than 300 symbols').optional(),
});

export const EditAccessGroupGeneralForm = ({ accessGroup, onUpdated }) => {
  const { id: accountId, groupId } = useParams();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { role: null, name: '', description: '' },
  });

  useEffect(() => {
    if (!accessGroup) {
      return;
    }

    reset({
      role: getGroupRoleSelectOption(accessGroup.role),
      name: accessGroup.name ?? '',
      description: accessGroup.description ?? '',
    });
  }, [accessGroup, reset]);

  const [updateAccessGroup, { loading }] = useMutation(UPDATE_ACCESS_GROUP, {
    onCompleted: () => {
      toast.success('Success');
      onUpdated();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <form
      onSubmit={handleSubmit((formData) =>
        updateAccessGroup({
          variables: {
            input: {
              id: Number(groupId),
              name: formData.name.trim(),
              role: formData.role.value,
              description: formData.description?.trim() ? formData.description.trim() : null,
            },
          },
        })
      )}
    >
      <Box direction="column" gap="16px" maxWidth="600px">
        <TextField id="id" label="ID" isDisabled>
          <Input width="100%" name="id" value={accessGroup?.id ?? ''} isDisabled />
        </TextField>

        <Controller
          name="role"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              id="role"
              label="Role"
              isError={Boolean(errors.role)}
              helperText={errors.role?.message || errors.role?.value?.message || ''}
            >
              <SelectSingle
                value={value}
                onChange={onChange}
                options={groupRoleSelectOptions}
                placeholder="Select role"
              />
            </TextField>
          )}
        />

        <Controller
          name="name"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              id="name"
              label="Group name"
              isError={Boolean(errors.name)}
              helperText={errors.name?.message || ''}
            >
              <Input
                width="100%"
                name="name"
                onChange={onChange}
                value={value}
                placeholder="Company team"
              />
            </TextField>
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              id="description"
              label="Description"
              isError={Boolean(errors.description)}
              helperText={errors.description?.message || ''}
            >
              <TextArea
                width="100%"
                onChange={onChange}
                value={value}
                placeholder="Briefly describe the purpose of this group"
              />
            </TextField>
          )}
        />

        <Box width="100%" gap="16px" justify="left">
          <Button
            type="submit"
            variant="solid"
            color="accent"
            isDisabled={loading}
            isLoading={loading}
          >
            Save
          </Button>
          <Button
            variant="outline"
            color="neutral"
            type="button"
            isDisabled={loading}
            onClick={() => navigate(`${getUsersAndGroupsPath(accountId)}?tab=accessGroups`)}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};
